/*
    🔥 THEME APP CONTEXT
        Contexto de apariencia global controlado por configuración de entorno.
        El tema inicial proviene de APP_CONFIG_GLOBAL.TEMPLATES.THEME_MODE y la posibilidad
        de cambiarlo desde el header depende de APP_CONFIG_GLOBAL.UTILITIES.MODE_LIGHT_MODE_DARK.

    ----------------------------------------------------------------------------
    🔥 MODOS
        light
            Fuerza apariencia clara.

        dark
            Fuerza apariencia oscura.

        system
            Sigue la preferencia del sistema operativo.

    ----------------------------------------------------------------------------
    🔥 PERSISTENCIA
        Cuando el selector de tema está habilitado, la elección se conserva en
        APP_STORAGE.APP_THEME. Si está deshabilitado, siempre gana el valor del .env.
*/

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FaDesktop, FaMoon, FaSun } from "react-icons/fa";
import { APP_ENVIRONMENT } from "../../_RuntimeConfig/_Config/Acomodar/AppEnvironment.js";
import { onApplyTheme, onReadTheme } from "../../../App/WEB/_Templates/AppTemplateResolved";

const ThemeContext = createContext(undefined);

const THEME_META = {
    light: { code: "light", label: "Claro", Icon: FaSun },
    dark: { code: "dark", label: "Oscuro", Icon: FaMoon },
    system: { code: "system", label: "Sistema", Icon: FaDesktop },
};

const ALL_THEMES = Object.keys(THEME_META);
const CAN_CHANGE_THEME = APP_ENVIRONMENT.UTILITIES.MODE_LIGHT_MODE_DARK;
const ENV_DEFAULT = String(APP_ENVIRONMENT.TEMPLATES.THEME_MODE || "DARK").toLowerCase();

const resolveActualTheme = (selected) => {
    if (selected !== "system") return selected;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const useTheme = () => {
    const context = useContext(ThemeContext);

    if (context !== undefined) return context;

    const selected = CAN_CHANGE_THEME ? onReadTheme() : ENV_DEFAULT;
    const resolved = resolveActualTheme(selected);

    return {
        useDataTheme: {
            isLoading: false,
            currentTheme: selected,
            resolvedTheme: resolved,
            availableThemes: CAN_CHANGE_THEME ? ALL_THEMES : [ENV_DEFAULT],
            availableThemesMeta: (CAN_CHANGE_THEME ? ALL_THEMES : [ENV_DEFAULT]).map((code) => THEME_META[code]).filter(Boolean),
        },
        call_isDark: () => resolved === "dark",
        call_getThemeMeta: (code) => THEME_META[code] ?? null,
        call_setTheme: () => selected,
        call_toggleTheme: () => selected,
        call_refreshTheme: () => selected,
    };
};

export const ThemeProvider = ({ children }) => {
    const [useNameCurrentThemeState, setNameCurrentThemeState] = useState(() => CAN_CHANGE_THEME ? onReadTheme() : ENV_DEFAULT);
    const [useNameResolvedThemeState, setNameResolvedThemeState] = useState(() => resolveActualTheme(useNameCurrentThemeState));

    const cb__applyThemeCallback = useCallback((theme, persist = true) => {
        const selected = CAN_CHANGE_THEME ? theme : ENV_DEFAULT;
        const applied = onApplyTheme(selected, { persist: CAN_CHANGE_THEME && persist });
        setNameCurrentThemeState(applied);
        setNameResolvedThemeState(resolveActualTheme(applied));
        return applied;
    }, []);

    useEffect(() => {
        cb__applyThemeCallback(useNameCurrentThemeState, false);
    }, [cb__applyThemeCallback, useNameCurrentThemeState]);

    useEffect(() => {
        if (!CAN_CHANGE_THEME || useNameCurrentThemeState !== "system" || typeof window.matchMedia !== "function") return undefined;

        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => cb__applyThemeCallback("system", false);
        media.addEventListener?.("change", onChange);
        return () => media.removeEventListener?.("change", onChange);
    }, [cb__applyThemeCallback, useNameCurrentThemeState]);

    const cb__setThemeCallback = useCallback((theme) => cb__applyThemeCallback(theme, true), [cb__applyThemeCallback]);

    const cb__toggleThemeCallback = useCallback(() => {
        if (!CAN_CHANGE_THEME) return useNameCurrentThemeState;
        const index = ALL_THEMES.indexOf(useNameCurrentThemeState);
        return cb__setThemeCallback(ALL_THEMES[(index + 1) % ALL_THEMES.length]);
    }, [cb__setThemeCallback, useNameCurrentThemeState]);

    const memo__themeContextValueMemo = useMemo(() => ({
        useDataTheme: {
            isLoading: false,
            currentTheme: useNameCurrentThemeState,
            resolvedTheme: useNameResolvedThemeState,
            availableThemes: CAN_CHANGE_THEME ? ALL_THEMES : [ENV_DEFAULT],
            availableThemesMeta: (CAN_CHANGE_THEME ? ALL_THEMES : [ENV_DEFAULT]).map((code) => THEME_META[code]).filter(Boolean),
        },
        call_isDark: () => useNameResolvedThemeState === "dark",
        call_getThemeMeta: (code) => THEME_META[code] ?? null,
        call_setTheme: cb__setThemeCallback,
        call_toggleTheme: cb__toggleThemeCallback,
        call_refreshTheme: () => cb__applyThemeCallback(useNameCurrentThemeState, false),
    }), [cb__applyThemeCallback, cb__setThemeCallback, cb__toggleThemeCallback, useNameCurrentThemeState, useNameResolvedThemeState]);

    return <ThemeContext.Provider value={memo__themeContextValueMemo}>{children}</ThemeContext.Provider>;
};
